/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { MfaRecoveryRequestDtov1 } from '../models/mfa-recovery-request-dtov-1';
import { RecoveryCodesDtov1 } from '../models/recovery-codes-dtov-1';
import { UserQueryDto } from '../models/user-query-dto';


/**
 * API for two-factor authentication setup.
 */
@Injectable({
  providedIn: 'root',
})
export class MfaV1Service extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation adminResetMfaV1
   */
  static readonly AdminResetMfaV1Path = '/v1/mfa/{userId}/admin-reset';

  /**
   * Reset a user's MFA enrollment (admin only).
   *
   * Disables the target user's software token MFA in Cognito and clears their recovery codes and enrollment date, requiring them to re-enroll on next login. Returns the updated user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `adminResetMfaV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  adminResetMfaV1$Response(params: {
    userId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<UserQueryDto>> {

    const rb = new RequestBuilder(this.rootUrl, MfaV1Service.AdminResetMfaV1Path, 'post');
    if (params) {
      rb.path('userId', params.userId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<UserQueryDto>;
      })
    );
  }

  /**
   * Reset a user's MFA enrollment (admin only).
   *
   * Disables the target user's software token MFA in Cognito and clears their recovery codes and enrollment date, requiring them to re-enroll on next login. Returns the updated user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `adminResetMfaV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  adminResetMfaV1(params: {
    userId: string;
  },
  context?: HttpContext

): Observable<UserQueryDto> {

    return this.adminResetMfaV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<UserQueryDto>) => r.body as UserQueryDto)
    );
  }

  /**
   * Path part for operation redeemMfaRecoveryCodeV1
   */
  static readonly RedeemMfaRecoveryCodeV1Path = '/v1/mfa/{tenantId}/recovery';

  /**
   * Redeem a recovery code to reset a lost authenticator.
   *
   * Validates the recovery code for the given email. On a match, disables the user's software token MFA in Cognito and clears their recovery codes, requiring re-enrollment on next login. Always returns 204 regardless of whether the email or code was valid, to prevent account enumeration.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `redeemMfaRecoveryCodeV1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  redeemMfaRecoveryCodeV1$Response(params: {

    /**
     * The unique identifier of the tenant
     */
    tenantId: string;
    body: MfaRecoveryRequestDtov1
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, MfaV1Service.RedeemMfaRecoveryCodeV1Path, 'post');
    if (params) {
      rb.path('tenantId', params.tenantId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Redeem a recovery code to reset a lost authenticator.
   *
   * Validates the recovery code for the given email. On a match, disables the user's software token MFA in Cognito and clears their recovery codes, requiring re-enrollment on next login. Always returns 204 regardless of whether the email or code was valid, to prevent account enumeration.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `redeemMfaRecoveryCodeV1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  redeemMfaRecoveryCodeV1(params: {

    /**
     * The unique identifier of the tenant
     */
    tenantId: string;
    body: MfaRecoveryRequestDtov1
  },
  context?: HttpContext

): Observable<void> {

    return this.redeemMfaRecoveryCodeV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation generateMfaRecoveryCodesV1
   */
  static readonly GenerateMfaRecoveryCodesV1Path = '/v1/mfa/recovery-codes';

  /**
   * Generate MFA recovery codes for the current user.
   *
   * Generates a fresh single-use set of recovery codes for the authenticated user, replacing any existing set. Callers must warn the user before invoking this again after initial setup, since it immediately invalidates previously issued codes.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `generateMfaRecoveryCodesV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  generateMfaRecoveryCodesV1$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RecoveryCodesDtov1>> {

    const rb = new RequestBuilder(this.rootUrl, MfaV1Service.GenerateMfaRecoveryCodesV1Path, 'post');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RecoveryCodesDtov1>;
      })
    );
  }

  /**
   * Generate MFA recovery codes for the current user.
   *
   * Generates a fresh single-use set of recovery codes for the authenticated user, replacing any existing set. Callers must warn the user before invoking this again after initial setup, since it immediately invalidates previously issued codes.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `generateMfaRecoveryCodesV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  generateMfaRecoveryCodesV1(params?: {
  },
  context?: HttpContext

): Observable<RecoveryCodesDtov1> {

    return this.generateMfaRecoveryCodesV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<RecoveryCodesDtov1>) => r.body as RecoveryCodesDtov1)
    );
  }

  /**
   * Path part for operation completeMfaSetupV1
   */
  static readonly CompleteMfaSetupV1Path = '/v1/mfa/complete-setup';

  /**
   * Complete MFA setup for the current user.
   *
   * Marks two-factor authentication setup as complete for the authenticated user. Fails with 409 if recovery codes have not been generated yet — the recovery-codes endpoint must be called first.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `completeMfaSetupV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  completeMfaSetupV1$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, MfaV1Service.CompleteMfaSetupV1Path, 'post');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Complete MFA setup for the current user.
   *
   * Marks two-factor authentication setup as complete for the authenticated user. Fails with 409 if recovery codes have not been generated yet — the recovery-codes endpoint must be called first.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `completeMfaSetupV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  completeMfaSetupV1(params?: {
  },
  context?: HttpContext

): Observable<void> {

    return this.completeMfaSetupV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

}
